import type { ExamItemAnswerTextCorrectionViewModel } from '@/shared/api/generated/types';

/** Entrada para a montagem do payload de correção de uma resposta */
export interface CorrectionPayloadInput {
  /** Detalhes da resposta carregados do backend */
  readonly details: ExamItemAnswerTextCorrectionViewModel;
  /** Completude atual do formulário (0–100) */
  readonly completeness: number;
  /** Comentário atual do formulário */
  readonly comment: string;
  /**
   * Nota que o corretor aplicou a partir da sugestão da IA nesta sessão, já
   * arredondada. `null` quando a IA não foi aplicada (ou não sugeriu nota).
   */
  readonly appliedAiGrade: number | null;
}

/**
 * Monta o payload de correção, decidindo se a flag `isAiAssisted` acompanha o envio.
 *
 * A flag só vai quando a nota enviada é exatamente a que a IA sugeriu e o corretor
 * aplicou. Se ele mexeu na completude depois de aplicar, a nota passa a ser dele e
 * o campo é **omitido** — assim como é omitido quando a IA nunca foi aplicada. A flag
 * que possa ter vindo nos detalhes de uma correção anterior é sempre descartada, para
 * que uma recorreção manual não herde o `true` do histórico.
 *
 * @param input - Detalhes da resposta, valores do formulário e nota aplicada pela IA
 * @returns Payload pronto para o endpoint de correção
 *
 * @example
 * ```ts
 * const payload = buildCorrectionPayload({
 *   details,
 *   completeness: 85,
 *   comment,
 *   appliedAiGrade: 85, // → isAiAssisted: true
 * });
 * ```
 */
export function buildCorrectionPayload({
  details,
  completeness,
  comment,
  appliedAiGrade,
}: CorrectionPayloadInput): ExamItemAnswerTextCorrectionViewModel {
  const { isAiAssisted: _previousFlag, ...rest } = details;

  const payload: ExamItemAnswerTextCorrectionViewModel = {
    ...rest,
    completness: completeness,
    comment,
  };

  const keptAiGrade = appliedAiGrade !== null && completeness === appliedAiGrade;

  return keptAiGrade ? { ...payload, isAiAssisted: true } : payload;
}
